import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  Eye,
  FileText,
  Loader2,
  Lock,
  LogIn,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  Unlock,
  X,
} from 'lucide-react';

import type { ExamAttemptDetail, ExamAttemptSummary, ExamSummary } from '../../shared/exams';
import { api, type Profile, type QuizSummary } from '../lib/api';
import { examUrl } from '../lib/exams';
import { useSubPath } from '../lib/useSubPath';
import { formatGrade, formatMinutes } from './ExamTake';

/**
 * Mis exámenes: crear los propios y ver las notas. Va dentro de «Mis trivias y
 * mis exámenes». Hacer un examen no pasa por aquí: se entra con su PIN, como a
 * una partida. Las notas se quedan guardadas hasta que quien creó el examen
 * las borre.
 */
export const ExamsView: React.FC<{
  profile: Profile | null;
  /** Cambia cada vez que se pide «Crear examen» desde la barra: abre el formulario. */
  createRequest?: number;
}> = ({ profile, createRequest = 0 }) => {
  const [exams, setExams] = useState<ExamSummary[] | null>(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  // El examen abierto va en la dirección (/mis-examenes/<id>).
  const [[openId], setSub] = useSubPath('/mis-examenes');
  const setOpenId = (id: string | null, opts?: { replace?: boolean }) => setSub(id ? [id] : [], opts);

  const load = useCallback(async () => {
    if (!profile) return;
    try {
      const data = await api.get<{ exams: ExamSummary[] }>('/exams/mine');
      setExams(data.exams);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar tus exámenes.');
    }
  }, [profile]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (createRequest > 0 && profile) {
      setOpenId(null);
      setCreating(true);
    }
  }, [createRequest, profile]);

  if (openId) {
    return (
      <ExamDetailView
        examId={openId}
        canMonitor={Boolean(profile?.canMonitor)}
        onBack={(opts) => {
          setOpenId(null, opts);
          void load();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {profile && (
        <section className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold">Mis exámenes</h2>
              <p className="text-ink-400 text-sm">
                Cada persona lo contesta a su ritmo, con el tiempo que tú fijes. Se entra con su PIN desde «Entrar con
                un PIN», como a una partida.
              </p>
            </div>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 px-4 h-10 rounded-lg font-semibold text-sm whitespace-nowrap shrink-0 bg-sky-600 hover:bg-sky-500 text-white"
            >
              <Plus className="w-4 h-4" /> Nuevo examen
            </button>
          </div>

          {error && <p className="text-danger-400 text-sm">{error}</p>}
          {!exams && !error && <Loader2 className="w-6 h-6 animate-spin text-ink-400" />}
          {exams && exams.length === 0 && (
            <p className="surface p-6 text-ink-400 text-sm text-center">
              Aún no has creado ningún examen. Se hacen a partir de una de tus trivias.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {exams?.map((e) => (
              <button
                key={e.id}
                onClick={() => setOpenId(e.id)}
                className="surface p-4 text-left hover:border-sky-500/60 transition-colors space-y-2"
              >
                <div className="flex items-start gap-2">
                  <p className="font-bold flex-1 min-w-0 truncate">{e.title}</p>
                  <span
                    className={`chip ${e.isOpen ? 'border-success-500/50 text-success-400' : 'border-ink-600 text-ink-400'}`}
                  >
                    {e.isOpen ? 'Abierto' : 'Cerrado'}
                  </span>
                </div>
                <p className="font-mono text-lg tracking-[0.2em] text-sky-300">{e.code}</p>
                <p className="text-ink-400 text-xs">
                  {e.questionCount} preguntas · {formatMinutes(e.durationMin)} · {e.attempts}{' '}
                  {e.attempts === 1 ? 'intento' : 'intentos'}
                  {e.inProgress > 0 ? ` (${e.inProgress} en curso)` : ''}
                  {e.averageGrade !== null ? ` · media ${formatGrade(e.averageGrade)}/${e.gradeScale}` : ''}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}

      {creating && profile && (
        <CreateExamModal
          canMonitor={Boolean(profile.canMonitor)}
          onClose={() => setCreating(false)}
          onCreated={(exam) => {
            setCreating(false);
            setOpenId(exam.id);
          }}
        />
      )}
    </div>
  );
};

/* ================================================================
 * Crear
 * ================================================================ */

const DURATIONS = [10, 15, 20, 30, 45, 60, 90, 120];
const SCALES = [10, 20, 100];

const CreateExamModal: React.FC<{
  canMonitor: boolean;
  onClose: () => void;
  onCreated: (exam: ExamSummary) => void;
}> = ({ canMonitor, onClose, onCreated }) => {
  const [quizzes, setQuizzes] = useState<QuizSummary[] | null>(null);
  const [quizId, setQuizId] = useState('');
  const [title, setTitle] = useState('');
  const [settings, setSettings] = useState<SettingsValue>({
    durationMin: 30,
    gradeScale: 100,
    randomize: true,
    showResults: true,
    monitor: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void api
      .get<{ quizzes: QuizSummary[] }>('/quizzes/mine')
      .then((d) => setQuizzes(d.quizzes))
      .catch(() => setError('No se pudieron cargar tus trivias.'));
  }, []);

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await api.post<{ exam: ExamSummary }>('/exams', { quizId, title, ...settings });
      onCreated(data.exam);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el examen.');
      setBusy(false);
    }
  };

  return (
    <Modal title="Nuevo examen" onClose={onClose}>
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Trivia de la que salen las preguntas</span>
          {!quizzes ? (
            <Loader2 className="w-5 h-5 animate-spin text-ink-400" />
          ) : quizzes.length === 0 ? (
            <p className="text-sm text-ink-400">Primero crea una trivia: el examen usa sus preguntas.</p>
          ) : (
            <select
              value={quizId}
              onChange={(e) => {
                setQuizId(e.target.value);
                const q = quizzes.find((x) => x.id === e.target.value);
                if (q && !title) setTitle(q.title);
              }}
              className={inputClass}
            >
              <option value="">Elige una trivia…</option>
              {quizzes.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.coverEmoji} {q.title} ({q.questionCount})
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Título del examen</span>
          <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 120))} className={inputClass} />
        </label>

        <SettingsFields value={settings} onChange={setSettings} canMonitor={canMonitor} />

        <p className="text-xs text-ink-500">
          Las preguntas se copian al crear el examen: si luego editas la trivia, el examen no cambia.
        </p>

        {error && <p className="text-danger-400 text-sm">{error}</p>}
        <button
          onClick={() => void create()}
          disabled={!quizId || busy}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Crear y obtener PIN
        </button>
      </div>
    </Modal>
  );
};

interface SettingsValue {
  durationMin: number;
  gradeScale: number;
  randomize: boolean;
  showResults: boolean;
  monitor: boolean;
}

const SettingsFields: React.FC<{
  value: SettingsValue;
  onChange: (v: SettingsValue) => void;
  canMonitor: boolean;
}> = ({ value, onChange, canMonitor }) => {
  const set = <K extends keyof SettingsValue>(k: K, v: SettingsValue[K]) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Duración (minutos)</span>
          <input
            type="number"
            min={1}
            max={600}
            list="exam-durations"
            value={value.durationMin}
            onChange={(e) => set('durationMin', Math.max(0, Math.round(Number(e.target.value) || 0)))}
            className={inputClass}
          />
          <datalist id="exam-durations">
            {DURATIONS.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Nota máxima</span>
          <input
            type="number"
            min={1}
            max={1000}
            list="exam-scales"
            value={value.gradeScale}
            onChange={(e) => set('gradeScale', Math.max(0, Math.round(Number(e.target.value) || 0)))}
            className={inputClass}
          />
          <datalist id="exam-scales">
            {SCALES.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
      </div>
      <Toggle
        checked={value.randomize}
        onChange={(v) => set('randomize', v)}
        label="Barajar preguntas y opciones"
        hint="Cada persona las ve en otro orden: copiar del de al lado sirve de poco."
      />
      <Toggle
        checked={value.showResults}
        onChange={(v) => set('showResults', v)}
        label="Enseñar la nota al entregar"
        hint="Si lo apagas, solo tú la ves."
      />
      {canMonitor && (
        <Toggle
          checked={value.monitor}
          onChange={(v) => set('monitor', v)}
          label="Grabar la pantalla (supervisión)"
          hint="Como en las salas supervisadas: se pide permiso antes de empezar y la grabación aparece en Grabaciones."
        />
      )}
      <p className="text-xs text-ink-500">
        En todos los exámenes el texto no se puede copiar y se cuenta cuántas veces cada persona sale de la pestaña,
        cambia de ventana, copia o pega.
      </p>
    </div>
  );
};

export const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }> = ({
  checked,
  onChange,
  label,
  hint,
}) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 w-4 h-4 accent-sky-500"
    />
    <span>
      <span className="text-sm text-ink-100">{label}</span>
      {hint && <span className="block text-xs text-ink-500">{hint}</span>}
    </span>
  </label>
);

/* ================================================================
 * Detalle y notas
 * ================================================================ */

const ExamDetailView: React.FC<{
  examId: string;
  canMonitor: boolean;
  /** `replace`: el examen ya no existe, su dirección no se queda en el historial. */
  onBack: (opts?: { replace?: boolean }) => void;
}> = ({
  examId,
  canMonitor,
  onBack,
}) => {
  const [exam, setExam] = useState<ExamSummary | null>(null);
  const [attempts, setAttempts] = useState<ExamAttemptSummary[]>([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [editing, setEditing] = useState(false);
  // El intento abierto también (/mis-examenes/<id>/intento/<id>).
  const [[, kind, attemptSeg], setSub] = useSubPath('/mis-examenes');
  const viewing = kind === 'intento' ? attemptSeg ?? null : null;
  const setViewing = (id: string | null) =>
    setSub(id ? [examId, 'intento', id] : [examId], { keepScroll: true });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ exam: ExamSummary; attempts: ExamAttemptSummary[] }>(`/exams/${examId}`);
      setExam(data.exam);
      setAttempts(data.attempts);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el examen.');
    }
  }, [examId]);

  useEffect(() => {
    void load();
    // Mientras haya gente haciéndolo, las notas van apareciendo solas.
    const id = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(id);
  }, [load]);

  const patch = async (body: Partial<SettingsValue & { isOpen: boolean; title: string }>) => {
    try {
      const data = await api.patch<{ exam: ExamSummary }>(`/exams/${examId}`, body);
      setExam(data.exam);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    }
  };

  const copy = async (what: 'code' | 'link') => {
    if (!exam) return;
    try {
      await navigator.clipboard.writeText(what === 'code' ? exam.code : examUrl(exam.code));
      setCopied(what);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* el navegador no deja copiar: el PIN está a la vista */
    }
  };

  const removeAttempt = async (a: ExamAttemptSummary) => {
    if (!confirm(`¿Borrar el intento y la nota de ${a.firstName} ${a.lastName}? No se puede deshacer.`)) return;
    await api.delete(`/exams/${examId}/attempts/${a.id}`);
    void load();
  };

  const removeExam = async () => {
    await api.delete(`/exams/${examId}`);
    onBack({ replace: true });
  };

  const exportCsv = () => {
    if (!exam) return;
    const rows = [
      ['Apellido', 'Nombre', 'Estado', 'Nota', 'Nota máxima', 'Puntos', 'Puntos máximos', 'Contestadas', 'Inicio', 'Entrega', 'Salidas de pestaña', 'Pérdidas de foco', 'Copias', 'Pegados'],
      ...attempts.map((a) => [
        a.lastName,
        a.firstName,
        statusLabel(a),
        a.grade ?? '',
        exam.gradeScale,
        a.score ?? '',
        a.maxScore ?? '',
        a.answered,
        new Date(a.startedAt).toLocaleString('es'),
        a.submittedAt ? new Date(a.submittedAt).toLocaleString('es') : '',
        a.alerts.hidden,
        a.alerts.blur,
        a.alerts.copy,
        a.alerts.paste,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `notas-${exam.code}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!exam) {
    return (
      <div className="space-y-4">
        <BackButton onClick={() => onBack()} />
        {error ? <p className="text-danger-400">{error}</p> : <Loader2 className="w-6 h-6 animate-spin text-ink-400" />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton onClick={() => onBack()} />

      <div className="surface-raised p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-display font-bold">{exam.title}</h1>
            <p className="text-ink-400 text-sm mt-1">
              {exam.questionCount} preguntas · {formatMinutes(exam.durationMin)} · nota sobre {exam.gradeScale}
              {exam.randomize ? ' · barajado' : ''}
              {exam.monitor ? ' · con grabación' : ''}
              {exam.showResults ? '' : ' · nota oculta al alumno'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-ink-500 text-xs uppercase tracking-wider">PIN</p>
            <p className="font-mono text-3xl font-black tracking-[0.25em] text-sky-300">{exam.code}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <SmallButton onClick={() => void copy('code')} icon={copied === 'code' ? <Check /> : <Copy />}>
            Copiar PIN
          </SmallButton>
          <SmallButton onClick={() => void copy('link')} icon={copied === 'link' ? <Check /> : <Copy />}>
            Copiar enlace
          </SmallButton>
          <SmallButton
            onClick={() => void patch({ isOpen: !exam.isOpen })}
            icon={exam.isOpen ? <Lock /> : <Unlock />}
          >
            {exam.isOpen ? 'Cerrar examen' : 'Volver a abrir'}
          </SmallButton>
          <SmallButton onClick={() => setEditing(true)} icon={<Settings />}>
            Ajustes
          </SmallButton>
          <SmallButton onClick={() => setConfirmDelete(true)} icon={<Trash2 />} danger>
            Borrar examen
          </SmallButton>
        </div>
        <p className="text-xs text-ink-500">
          {exam.isOpen
            ? 'Abierto: cualquiera con el PIN puede empezarlo desde «Entrar con un PIN».'
            : 'Cerrado: nadie nuevo puede empezarlo. Quien ya estaba dentro termina con su tiempo.'}
        </p>
        {error && <p className="text-danger-400 text-sm">{error}</p>}
      </div>

      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold flex-1">
            Notas <span className="text-ink-500 text-base font-normal">({attempts.length})</span>
          </h2>
          <SmallButton onClick={() => void load()} icon={<RefreshCw />}>
            Actualizar
          </SmallButton>
          <SmallButton onClick={exportCsv} icon={<Download />} disabled={attempts.length === 0}>
            Exportar CSV
          </SmallButton>
        </div>

        {attempts.length === 0 ? (
          <p className="surface p-6 text-ink-400 text-sm text-center">
            Todavía nadie ha hecho el examen. Comparte el PIN <strong className="font-mono">{exam.code}</strong>.
          </p>
        ) : (
          <div className="surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-ink-500 text-xs uppercase tracking-wider">
                <tr className="border-b border-ink-700">
                  <th className="text-left p-3">Persona</th>
                  <th className="text-left p-3">Estado</th>
                  <th className="text-right p-3">Nota</th>
                  <th className="text-right p-3">Contestadas</th>
                  <th className="text-left p-3">Avisos</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.id} className="border-b border-ink-800 last:border-0">
                    <td className="p-3">
                      <p className="font-semibold">
                        {a.lastName}, {a.firstName}
                      </p>
                      <p className="text-ink-500 text-xs">{new Date(a.startedAt).toLocaleString('es')}</p>
                    </td>
                    <td className="p-3">
                      <span className={a.submittedAt ? 'text-ink-300' : 'text-sky-400'}>{statusLabel(a)}</span>
                    </td>
                    <td className="p-3 text-right font-bold tabular-nums">
                      {a.grade !== null ? `${formatGrade(a.grade)} / ${exam.gradeScale}` : '—'}
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {a.answered} / {exam.questionCount}
                    </td>
                    <td className="p-3">
                      <AlertsCell attempt={a} />
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setViewing(a.id)}
                          aria-label="Ver respuestas"
                          className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => void removeAttempt(a)}
                          aria-label="Borrar intento"
                          className="p-2 rounded-lg text-ink-400 hover:text-danger-400 hover:bg-ink-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-ink-500">
          Las notas se guardan hasta que tú las borres.
          {exam.monitor ? ' Las grabaciones de pantalla están en Grabaciones y se borran solas a los 30 días.' : ''}
        </p>
      </section>

      {editing && (
        <EditExamModal
          exam={exam}
          canMonitor={canMonitor}
          onClose={() => setEditing(false)}
          onSave={async (body) => {
            await patch(body);
            setEditing(false);
          }}
        />
      )}

      {viewing && <AttemptModal examId={examId} attemptId={viewing} onClose={() => setViewing(null)} />}

      {confirmDelete && (
        <Modal title="¿Borrar el examen?" onClose={() => setConfirmDelete(false)}>
          <p className="text-ink-300 text-sm">
            Se borrarán el examen y <strong>todas sus notas</strong> ({attempts.length}). No se puede deshacer. Si solo
            quieres que nadie más entre, mejor ciérralo.
          </p>
          <div className="flex gap-3 mt-5">
            <button
              onClick={() => setConfirmDelete(false)}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100"
            >
              Cancelar
            </button>
            <button
              onClick={() => void removeExam()}
              className="flex-1 px-4 py-3 rounded-xl font-bold bg-danger-500 hover:bg-danger-400 text-white"
            >
              Borrar todo
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

function statusLabel(a: ExamAttemptSummary): string {
  if (!a.submittedAt) return 'En curso';
  return a.finishReason === 'expired' ? 'Tiempo agotado' : 'Entregado';
}

const AlertsCell: React.FC<{ attempt: ExamAttemptSummary }> = ({ attempt }) => {
  const { hidden, blur, copy, paste } = attempt.alerts;
  const parts = [
    hidden && `${hidden} salida${hidden === 1 ? '' : 's'}`,
    blur && `${blur} foco`,
    copy && `${copy} copia${copy === 1 ? '' : 's'}`,
    paste && `${paste} pegado${paste === 1 ? '' : 's'}`,
  ].filter(Boolean);
  return (
    <span className={parts.length ? 'text-ember-300 text-xs' : 'text-ink-500 text-xs'}>
      {parts.length ? parts.join(' · ') : 'Ninguno'}
      {attempt.recorded && <span className="block text-sky-400">Grabado</span>}
    </span>
  );
};

const EditExamModal: React.FC<{
  exam: ExamSummary;
  canMonitor: boolean;
  onClose: () => void;
  onSave: (body: SettingsValue & { title: string }) => Promise<void>;
}> = ({ exam, canMonitor, onClose, onSave }) => {
  const [title, setTitle] = useState(exam.title);
  const [settings, setSettings] = useState<SettingsValue>({
    durationMin: exam.durationMin,
    gradeScale: exam.gradeScale,
    randomize: exam.randomize,
    showResults: exam.showResults,
    monitor: exam.monitor,
  });
  return (
    <Modal title="Ajustes del examen" onClose={onClose}>
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Título</span>
          <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 120))} className={inputClass} />
        </label>
        <SettingsFields value={settings} onChange={setSettings} canMonitor={canMonitor} />
        <p className="text-xs text-ink-500">
          Cambiar la duración no afecta a quien ya empezó. Cambiar la nota máxima recalcula las notas ya puestas.
        </p>
        <button
          onClick={() => void onSave({ ...settings, title })}
          className="w-full py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
};

const AttemptModal: React.FC<{ examId: string; attemptId: string; onClose: () => void }> = ({
  examId,
  attemptId,
  onClose,
}) => {
  const [detail, setDetail] = useState<ExamAttemptDetail | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    void api
      .get<ExamAttemptDetail>(`/exams/${examId}/attempts/${attemptId}`)
      .then(setDetail)
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudo cargar.'));
  }, [examId, attemptId]);

  return (
    <Modal
      title={detail ? `${detail.attempt.firstName} ${detail.attempt.lastName}` : 'Respuestas'}
      onClose={onClose}
      wide
    >
      {error && <p className="text-danger-400 text-sm">{error}</p>}
      {!detail && !error && <Loader2 className="w-6 h-6 animate-spin text-ink-400" />}
      {detail && (
        <div className="space-y-3">
          {!detail.attempt.submittedAt && (
            <p className="text-sky-400 text-sm">Todavía lo está haciendo: esto es lo que lleva hasta ahora.</p>
          )}
          {detail.questions.map((q, i) => (
            <div key={q.id} className="rounded-xl border border-ink-700 bg-ink-850 p-3 space-y-1">
              <div className="flex items-start gap-2">
                <span className="text-ink-500 text-sm tabular-nums">{i + 1}.</span>
                <p className="flex-1 font-semibold text-sm">{q.title}</p>
                <span
                  className={`chip text-xs ${
                    q.answer === null
                      ? 'border-ink-600 text-ink-400'
                      : q.correctness >= 1
                        ? 'border-success-500/50 text-success-400'
                        : q.correctness > 0
                          ? 'border-ember-500/50 text-ember-300'
                          : 'border-danger-500/50 text-danger-400'
                  }`}
                >
                  {q.answer === null
                    ? 'En blanco'
                    : q.correctness >= 1
                      ? 'Correcta'
                      : q.correctness > 0
                        ? `Parcial ${Math.round(q.correctness * 100)}%`
                        : 'Incorrecta'}
                </span>
              </div>
              <p className="text-xs text-ink-400 pl-5">
                Respondió: <span className="text-ink-200">{q.answer ?? '—'}</span>
              </p>
              <p className="text-xs text-ink-400 pl-5">
                Correcta: <span className="text-success-400">{q.solution}</span>
                {q.weight !== 1 ? ` · vale ${q.weight}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

/* ================================================================ */

export const inputClass =
  'w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500';

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
    <ArrowLeft className="w-4 h-4" /> Mis exámenes
  </button>
);

export const SmallButton: React.FC<{
  onClick: () => void;
  icon: React.ReactElement<{ className?: string }>;
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
}> = ({ onClick, icon, children, danger, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 ${
      danger
        ? 'border-danger-500/40 text-danger-400 hover:bg-danger-500/10'
        : 'border-ink-700 bg-ink-850 text-ink-200 hover:bg-ink-800'
    }`}
  >
    {React.cloneElement(icon, { className: 'w-4 h-4' })}
    {children}
  </button>
);

export const Modal: React.FC<{ title: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({
  title,
  onClose,
  wide,
  children,
}) => (
  <div
    className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className={`surface-raised w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto p-6 animate-pop`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xl font-bold flex-1">{title}</h2>
        <button onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800">
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);
